import type { ComponentType } from 'react'

import incomeIcon from 'app/assets/icons/summary_icons/income.svg'
import { ContainerHeader as ContainerHeaderBase } from 'app/components/display/text/standard_text/standard-headings/component'
import { FI_UPGRADE_CTA_TEXT } from 'app/match/apply/application_summary_before_submit/components/manager_income_financials_container/financial-insights.constants'
import {
  formatCurrency,
  formatNextPay,
  formatPercentage,
  getMissingEssentialsLabel,
} from 'app/utils/financial/formatters'

import 'app/match/apply/application_summary_before_submit/components/manager_income_financials_container/style.scss'

// ContainerHeader is a legacy .jsx component whose TS inference marks props as
// required when they have no default values, though all are truly optional at
// runtime. Cast to an all-optional interface so the call site only needs to
// pass the meaningful props.
const ContainerHeader = ContainerHeaderBase as ComponentType<{
  icon?: string
  text?: string
  textClass?: string
  isEditable?: boolean
  onEditClick?: (() => void) | undefined
  editTitle?: string
  componentClass?: string
  onNewNoteUpdated?: (() => void) | undefined
  newNotesContent?: unknown
  onNoteIconClicked?: (() => void) | undefined
  onNewNoteAdded?: (() => void) | undefined
  onNoteCrossClicked?: (() => void) | undefined
  interpreteNoteResult?: Record<string, unknown> | undefined
  displayedApplicant?: Record<string, unknown> | undefined
  section?: string | undefined
  enableNotesButton?: boolean | undefined
  editBtnClass?: string
  toggleRequestInfoModal?: (() => void) | undefined
  isManagerAdded?: boolean | undefined
  toggleEditRenterApplication?: unknown
  targetGUID?: unknown
  iconHeaderCmp?: unknown
}>

const INCOME_COMPARISON_THRESHOLD = 0.15
const WEEKS_PER_MONTH = 4.33

interface VerifiedIncome {
  isRedacted?: boolean
  hasFinancialInsights: boolean
  verifiedMonthlyIncome?: number
  verifiedSavingsTotal?: number
  rentToIncomeRatio?: number
  financialInsightsDate?: string
  weeklySurplusAfterRentAndFinances?: number | null
  incomeStabilityDuration?: string | null
  incomeConfidence?: string | null
  nextPayDate?: string | null
  nextPayAmount?: number | null
  missingEssentialsGroceries?: boolean | null
  missingEssentialsTransport?: boolean | null
  missingEssentialsAccommodation?: boolean | null
}

interface Props {
  verifiedIncome?: VerifiedIncome | null
  selfReportedWeeklyIncome?: number | null
  displayedApplicant?: Record<string, unknown>
  onNewNoteUpdated?: () => void
  newNotesContent?: unknown
  onNoteIconClicked?: () => void
  onNewNoteAdded?: () => void
  onNoteCrossClicked?: () => void
  interpreteNoteResult?: Record<string, unknown>
  enableNotesButton?: boolean
  section?: string
  id?: string
  toggleRequestInfoModal?: () => void
  isManagerAdded?: boolean
}

function getComparisonStatus(
  selfReportedWeekly?: number | null,
  verifiedMonthly?: number | null,
): 'differ' | 'match' | null {
  if (selfReportedWeekly == null || !verifiedMonthly) return null
  const selfReportedMonthly = selfReportedWeekly * WEEKS_PER_MONTH
  const diff = Math.abs(selfReportedMonthly - verifiedMonthly) / verifiedMonthly
  return diff > INCOME_COMPARISON_THRESHOLD ? 'differ' : 'match'
}

interface VerifiedMetricsProps {
  verifiedIncome: VerifiedIncome
  selfReportedWeeklyIncome?: number | null
  comparison: 'differ' | 'match' | null
}

function VerifiedMetrics({
  verifiedIncome,
  selfReportedWeeklyIncome,
  comparison,
}: VerifiedMetricsProps) {
  const nextPay = formatNextPay(
    verifiedIncome.nextPayDate,
    verifiedIncome.nextPayAmount,
  )
  const missingEssentials = getMissingEssentialsLabel(
    verifiedIncome.missingEssentialsGroceries,
    verifiedIncome.missingEssentialsTransport,
    verifiedIncome.missingEssentialsAccommodation,
  )

  return (
    <div className="application-container-padding">
      <div className="verified-metrics">
        <div className="metric-row">
          <span className="income-category income-label">
            Verified monthly income
          </span>
          <span className="metric-value">
            {formatCurrency(verifiedIncome.verifiedMonthlyIncome)}
          </span>
        </div>
        {verifiedIncome.verifiedSavingsTotal != null && (
          <div className="metric-row">
            <span className="income-category income-label">Savings total</span>
            <span className="metric-value">
              {formatCurrency(verifiedIncome.verifiedSavingsTotal)}
            </span>
          </div>
        )}
        <div className="metric-row">
          <span className="income-category income-label">
            Rent-to-income ratio
          </span>
          <span className="metric-value">
            {formatPercentage(verifiedIncome.rentToIncomeRatio)}
          </span>
        </div>
        {verifiedIncome.financialInsightsDate && (
          <div className="metric-row">
            <span className="income-category income-label">Verified date</span>
            <span className="metric-value">
              {verifiedIncome.financialInsightsDate}
            </span>
          </div>
        )}
        {verifiedIncome.weeklySurplusAfterRentAndFinances != null && (
          <div className="metric-row">
            <span className="income-category income-label">
              Weekly surplus after rent &amp; commitments
            </span>
            <span className="metric-value">
              {formatCurrency(verifiedIncome.weeklySurplusAfterRentAndFinances)}
              /wk
            </span>
          </div>
        )}
        {verifiedIncome.incomeStabilityDuration != null && (
          <div className="metric-row">
            <span className="income-category income-label">
              Income stability
            </span>
            <span className="metric-value">
              {verifiedIncome.incomeStabilityDuration}
            </span>
          </div>
        )}
        {verifiedIncome.incomeConfidence != null && (
          <div className="metric-row">
            <span className="income-category income-label">
              Income confidence
            </span>
            <span className="metric-value">
              {verifiedIncome.incomeConfidence}
            </span>
          </div>
        )}
        {nextPay != null && (
          <div className="metric-row">
            <span className="income-category income-label">Next pay</span>
            <span className="metric-value">{nextPay}</span>
          </div>
        )}
        <div className="metric-row">
          <span className="income-category income-label">
            Missing essentials
          </span>
          <span className="metric-value">{missingEssentials}</span>
        </div>
      </div>

      {comparison && (
        <div className="income-comparison">
          <span className="income-category income-label">
            Income comparison
          </span>
          <div className="comparison-rows">
            <div className="comparison-row">
              <span className="comparison-label">Self-reported:</span>
              <span className="comparison-value">
                {formatCurrency(
                  selfReportedWeeklyIncome != null
                    ? selfReportedWeeklyIncome * WEEKS_PER_MONTH
                    : null,
                )}
                /mo
              </span>
            </div>
            <div className="comparison-row">
              <span className="comparison-label">Verified:</span>
              <span className="comparison-value">
                {formatCurrency(verifiedIncome.verifiedMonthlyIncome)}/mo
              </span>
            </div>
            <div className={`comparison-indicator ${comparison}`}>
              {comparison === 'match'
                ? 'Income figures are consistent'
                : 'Income figures differ significantly'}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const ManagerIncomeFinancialsContainer = ({
  verifiedIncome,
  selfReportedWeeklyIncome,
  displayedApplicant,
  onNewNoteUpdated,
  newNotesContent,
  onNoteIconClicked,
  onNewNoteAdded,
  onNoteCrossClicked,
  interpreteNoteResult = {},
  enableNotesButton,
  section = 'verifiedFinancials',
  id = '',
  toggleRequestInfoModal,
  isManagerAdded,
}: Props) => {
  if (!verifiedIncome) return null
  if (!verifiedIncome.isRedacted && !verifiedIncome.hasFinancialInsights)
    return null

  const comparison = verifiedIncome.isRedacted
    ? null
    : getComparisonStatus(
        selfReportedWeeklyIncome,
        verifiedIncome.verifiedMonthlyIncome,
      )

  return (
    <div
      id={id}
      className="container-component manager-income-financials-container bbs"
    >
      <ContainerHeader
        icon={incomeIcon}
        text="Verified Financial Insights"
        isEditable={false}
        displayedApplicant={displayedApplicant}
        onNewNoteUpdated={onNewNoteUpdated}
        newNotesContent={newNotesContent}
        onNoteIconClicked={onNoteIconClicked}
        onNewNoteAdded={onNewNoteAdded}
        onNoteCrossClicked={onNoteCrossClicked}
        interpreteNoteResult={interpreteNoteResult}
        enableNotesButton={enableNotesButton}
        section={section}
        toggleRequestInfoModal={toggleRequestInfoModal}
        isManagerAdded={isManagerAdded}
      />
      {verifiedIncome.isRedacted ? (
        <div className="application-container-padding">
          <p className="fi-upgrade-cta">{FI_UPGRADE_CTA_TEXT}</p>
        </div>
      ) : (
        <VerifiedMetrics
          verifiedIncome={verifiedIncome}
          selfReportedWeeklyIncome={selfReportedWeeklyIncome ?? null}
          comparison={comparison}
        />
      )}
    </div>
  )
}

export default ManagerIncomeFinancialsContainer
